import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format, parseISO, addDays } from "date-fns";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, Download, RefreshCw, CalendarIcon } from "lucide-react";
import { useInvoiceDetail } from "@/components/invoices/useInvoiceDetail";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { EditableSelect } from "@/components/ui/editable-select";
import { EditableInvoiceTable } from "@/components/ui/editable-invoice-table";
import { EditableItemList, ItemNote } from "@/components/ui/editable-item-list";
import { invoiceEditSchema, InvoiceEditValues, statusColors, statusOptions, statusLabels } from "@/types/invoice";
import { generateInvoicePDF } from "@/utils/pdfUtils";
import { useToast } from "@/hooks/use-toast";

const InvoiceDetail = () => {
  const {
    invoiceId
  } = useParams<{
    invoiceId: string;
  }>();
  const {
    toast
  } = useToast();
  const navigate = useNavigate();
  const [editingField, setEditingField] = useState<string | null>(null);
  const invoicePrintRef = useRef<HTMLDivElement>(null);
  const [itemNotes, setItemNotes] = useState<ItemNote[]>([]);
  const {
    invoice,
    client,
    company,
    invoiceItems,
    isLoading,
    error,
    updateInvoice,
    isUpdating,
    updateInvoiceItem,
    deleteInvoiceItem,
    addInvoiceItem,
    syncAssignments
  } = useInvoiceDetail(invoiceId);
  const form = useForm<InvoiceEditValues>({
    resolver: zodResolver(invoiceEditSchema),
    defaultValues: {
      amount: 0,
      status: 'draft',
      currency: 'USD',
      billing_period_start: '',
      billing_period_end: '',
      sent_at: null
    }
  });
  useEffect(() => {
    if (invoice) {
      form.reset({
        amount: invoice.amount,
        status: invoice.status,
        currency: invoice.currency,
        billing_period_start: invoice.billing_period_start,
        billing_period_end: invoice.billing_period_end,
        sent_at: invoice.sent_at,
        item_notes: invoice.item_notes
      });

      // Initialize item notes from invoice.item_notes if available
      if (invoice.item_notes) {
        try {
          const notes = typeof invoice.item_notes === 'string' 
            ? JSON.parse(invoice.item_notes) 
            : invoice.item_notes;
          
          setItemNotes(Array.isArray(notes) ? notes : []);
        } catch (e) {
          // If parsing fails, initialize with a default note containing the string
          setItemNotes([{ id: 1, content: String(invoice.item_notes) }]);
        }
      } else {
        setItemNotes([{ id: 1, content: '' }]);
      }
    }
  }, [invoice, form]);
  const handleSaveField = async (field: string, value: any) => {
    if (!invoice) return;
    const currentValues = form.getValues();
    const updatedValues = {
      ...currentValues,
      [field]: value
    };
    updateInvoice(updatedValues);
    setEditingField(null);
  };
  const calculateDueDate = (date: string) => {
    const paymentTerms = client?.billing_details?.paymentTerms ? parseInt(client.billing_details.paymentTerms, 10) : 10; // Default to 10 days if not specified

    return format(addDays(parseISO(date), paymentTerms), 'LLL d, yyyy');
  };
  const handleDownloadPDF = async () => {
    if (!invoice) return;
    try {
      await generateInvoicePDF(invoicePrintRef, invoice);
      toast({
        title: "Invoice Downloaded",
        description: `Invoice ${invoice.invoice_number} has been downloaded as PDF`
      });
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast({
        title: "Download Failed",
        description: "There was an error generating the PDF",
        variant: "destructive"
      });
    }
  };
  const formatCurrency = (amount: number, currencyCode: string) => {
    const currencySymbols: Record<string, string> = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'JPY': '¥',
      'CNY': '¥',
      'INR': '₹',
      'BRL': 'R$',
      'MXN': '$',
      'CAD': 'C$',
      'AUD': 'A$'
    };
    const symbol = currencySymbols[currencyCode] || currencyCode;
    return `${symbol}${amount.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })}`;
  };

  const handleUpdateNote = (id: number, value: string) => {
    setItemNotes(prev => {
      const updated = prev.map(note => 
        note.id === id ? { ...note, content: value } : note
      );
      
      // Update the invoice with the new notes
      updateInvoice({
        ...form.getValues(),
        item_notes: updated
      });
      
      return updated;
    });
  };
  
  const handleDeleteNote = (id: number) => {
    setItemNotes(prev => {
      const updated = prev.filter(note => note.id !== id);
      
      // Update the invoice with the new notes
      updateInvoice({
        ...form.getValues(),
        item_notes: updated
      });
      
      return updated;
    });
  };
  
  const handleAddNote = () => {
    setItemNotes(prev => {
      const newId = prev.length > 0 ? Math.max(...prev.map(note => note.id)) + 1 : 1;
      const updated = [...prev, { id: newId, content: '' }];
      
      // Update the invoice with the new notes
      updateInvoice({
        ...form.getValues(),
        item_notes: updated
      });
      
      return updated;
    });
  };

  if (isLoading) {
    return <div className="p-8">
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="icon" onClick={() => navigate('/invoices')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">Loading Invoice...</h1>
        </div>
      </div>;
  }
  if (error || !invoice) {
    return <div className="p-8">
        <div className="flex items-center space-x-2 mb-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/invoices')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">Invoice Not Found</h1>
        </div>
        <Button onClick={() => navigate('/invoices')}>Back to Invoices</Button>
      </div>;
  }
  const paymentTerms = client?.billing_details?.paymentTerms ? parseInt(client.billing_details.paymentTerms, 10) : 10; // Default to 10 days if not specified

  return <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="icon" onClick={() => navigate('/invoices')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">
            Invoice: {invoice.invoice_number}
          </h1>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" onClick={syncAssignments} disabled={invoice.status === 'paid' || invoice.status === 'sent'}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Sync
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownloadPDF}>
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>
        </div>
      </div>
      
      <div ref={invoicePrintRef} className="bg-white border rounded-lg shadow-sm p-8 max-w-4xl mx-auto">
        <div className="grid grid-cols-2 gap-12 mb-10">
          <div>
            {company?.logo_url ? <img src={company.logo_url} alt={company.name} className="h-24 w-auto mb-6" /> : <div className="h-24 w-48 bg-gray-100 flex items-center justify-center mb-6 rounded">
                <span className="text-gray-400">Company Logo</span>
              </div>}
            <div className="text-sm space-y-1">
              <p className="font-medium">{company?.name}</p>
              <p>{company?.address}</p>
            </div>
            
            {/* Bill to section */}
            <div className="mt-8">
              <h3 className="mb-1 font-normal text-gray-500">Bill to:</h3>
              <div className="text-sm space-y-1">
                <p>{client?.legal_name}</p>
                <p>{client?.billing_details?.address}</p>
              </div>
            </div>
          </div>
          
          <div className="text-right">
            <div className="mb-6">
              <h1 className="text-3xl font-bold uppercase">INVOICE</h1>
              <div className="text-xl font-bold mt-1">#{invoice.invoice_number}</div>
            </div>
            <div className="space-y-1">
              <div className="flex justify-end items-center">
                <span className="text-gray-600 inline-block w-24 text-right mr-2">Date:</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <span className="cursor-pointer">
                      {invoice.sent_at ? format(parseISO(invoice.sent_at), 'LLL d, yyyy') : 'Not sent yet'}
                    </span>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <Calendar mode="single" selected={invoice.sent_at ? parseISO(invoice.sent_at) : undefined} onSelect={date => {
                    if (date) {
                      const formattedDate = format(date, 'yyyy-MM-dd');
                      updateInvoice({
                        ...form.getValues(),
                        sent_at: formattedDate
                      });
                    }
                  }} initialFocus className="rounded-md border pointer-events-auto" />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="flex justify-end items-center">
                <span className="text-gray-600 inline-block w-24 text-right mr-2">Due Date:</span>
                <span>NET {paymentTerms}</span>
              </div>
              <div className="flex justify-end items-center mt-2">
                <Badge className={`status-badge ${statusColors[invoice.status]}`}>
                  {statusLabels[invoice.status]}
                </Badge>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mb-20">
          <EditableInvoiceTable items={invoiceItems} currency={invoice.currency} onUpdate={(id, field, value) => {
          updateInvoiceItem(id, field, value);
        }} onDelete={id => {
          deleteInvoiceItem(id);
        }} onAdd={addInvoiceItem} readOnly={invoice.status === 'paid' || invoice.status === 'sent'} pdfMode={false} />
        </div>
        
        {/* Add the EditableItemList component */}
        <div className="mb-10">
          <EditableItemList 
            items={itemNotes}
            onUpdate={handleUpdateNote}
            onDelete={handleDeleteNote}
            onAdd={handleAddNote}
            readOnly={invoice.status === 'paid' || invoice.status === 'sent'}
            pdfMode={false}
            title="Additional Notes"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-6">
          <div>
            <h2 className="text-sm text-gray-600 mb-2">Payment Information:</h2>
            <div className="text-sm space-y-1">
              {company?.bank_details?.account_number && <p><span className="font-medium">Bank Account:</span> {company.bank_details.account_number}</p>}
              {company?.bank_details?.recipient_name && <p><span className="font-medium">Recipient:</span> {company.bank_details.recipient_name} {company.bank_details.tax_id && <span>// Tax ID/VAT number: {company.bank_details.tax_id}</span>}</p>}
              {company?.bank_details?.bank_name && <p><span className="font-medium">Bank Name:</span> {company.bank_details.bank_name} {company.bank_details.swift_code && <span>// Swift Code: {company.bank_details.swift_code}</span>}</p>}
              {company?.bank_details?.location && <p><span className="font-medium">Location:</span> {company.bank_details.location}</p>}
            </div>
          </div>
          
          <div>
            <h2 className="text-sm text-gray-600 mb-2">Payment Notes:</h2>
            <p className="text-sm">All amounts are in {invoice.currency}</p>
          </div>
        </div>
      </div>
    </div>;
};
export default InvoiceDetail;
